import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { formatDateOnly, revenueResponseRowKey } from './formatters';

const FULL_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export async function downloadRevenueCampaignExcel(params: {
  campaignId: string;
  periodMonth: number;
  periodYear: number;
  campaignName: string | null;
}): Promise<void> {
  const { campaignId, periodMonth, periodYear, campaignName } = params;
  const { data: chData, error: chErr } = await supabase
    .from('revenue_collection_campaign_hotels')
    .select('hotel_id')
    .eq('campaign_id', campaignId);
  if (chErr) throw chErr;
  const hotelIds = (chData || []).map((ch) => ch.hotel_id);
  if (hotelIds.length === 0) {
    throw new Error('No hotels in this campaign to export.');
  }

  const { data: hotelRows } = await supabase
    .from('hotels')
    .select('id, title')
    .in('id', hotelIds);
  const hotelNameById = new Map((hotelRows || []).map((h) => [h.id, h.title ?? '']));

  const { data: groupRows, error: gErr } = await supabase
    .from('mv_group_enriched_summary')
    .select(
      'hotel_id, request_id, checkin_date, checkout_date, number_of_nights, company, client_request_reference, estimated_revenue',
    )
    .eq('arrival_month', periodMonth)
    .eq('arrival_year', periodYear)
    .in('hotel_id', hotelIds)
    .in('status', ['Booked', 'reserved'])
    .gt('estimated_revenue', 0);
  if (gErr) throw gErr;

  const { data: respRows, error: rErr } = await supabase
    .from('revenue_collection_responses')
    .select('hotel_id, request_id, checkin_date, realised_revenue, commission_percent')
    .eq('campaign_id', campaignId);
  if (rErr) throw rErr;

  const { data: hotelCommRows } = await supabase
    .from('revenue_collection_hotel_commissions')
    .select('hotel_id, commission_percent')
    .in('hotel_id', hotelIds);
  const hotelDefaultCommissionById = new Map<number, number>();
  (hotelCommRows || []).forEach((h: { hotel_id: number; commission_percent: number }) => {
    hotelDefaultCommissionById.set(h.hotel_id, Number(h.commission_percent));
  });

  const realisedByHotelAndKey = new Map<string, number | null>();
  const commissionOverrideByHotelAndKey = new Map<string, number | null>();
  (respRows || []).forEach(
    (r: {
      hotel_id: number;
      request_id: number;
      checkin_date: string;
      realised_revenue: unknown;
      commission_percent?: number | null;
    }) => {
      const k = `${r.hotel_id}|${revenueResponseRowKey(r.request_id, r.checkin_date)}`;
      realisedByHotelAndKey.set(
        k,
        r.realised_revenue != null && r.realised_revenue !== ''
          ? Number(r.realised_revenue)
          : null,
      );
      commissionOverrideByHotelAndKey.set(
        k,
        r.commission_percent == null ? null : Number(r.commission_percent),
      );
    },
  );

  const { data: manualExportRows } = await supabase
    .from('revenue_collection_manual_lines')
    .select(
      'hotel_id, client_name, ref_client, checkin_date, checkout_date, nights, estimated_revenue, realised_revenue, commission_percent',
    )
    .eq('campaign_id', campaignId)
    .in('hotel_id', hotelIds);

  const groupExportRows = (groupRows || []).map(
    (g: {
      hotel_id: number;
      request_id: number;
      checkin_date: string;
      checkout_date: string | null;
      number_of_nights: number | null;
      company: string | null;
      client_request_reference?: string | null;
      estimated_revenue: unknown;
    }) => {
      const rk = `${g.hotel_id}|${revenueResponseRowKey(g.request_id, g.checkin_date)}`;
      const realised = realisedByHotelAndKey.get(rk);
      const commOverride = commissionOverrideByHotelAndKey.get(rk);
      const commEffective =
        commOverride ?? hotelDefaultCommissionById.get(g.hotel_id) ?? '';
      const estimated = Number(g.estimated_revenue) || 0;
      const realisedForInvoice = realised ?? null;
      const pctNum = commEffective === '' ? null : Number(commEffective);
      const invoiceAmount =
        pctNum != null && !Number.isNaN(pctNum) && realisedForInvoice != null
          ? realisedForInvoice * (pctNum / 100)
          : '';
      return {
        Origin: 'Booked group',
        'Hotel ID': g.hotel_id,
        'Hotel name': hotelNameById.get(g.hotel_id) ?? '',
        'Group ID': g.request_id,
        Client: g.company ?? '',
        'Ref. client': g.client_request_reference?.trim() || '',
        'Check-in': formatDateOnly(g.checkin_date),
        'Check-out': g.checkout_date ? formatDateOnly(g.checkout_date) : '',
        Nights: g.number_of_nights == null ? '' : Number(g.number_of_nights),
        'Estimated Revenue': estimated,
        'Realised Revenue': realised ?? '',
        'Commission %': commEffective === '' ? '' : commEffective,
        'Invoice amount': invoiceAmount,
      };
    },
  );

  const manualSheetRows = (manualExportRows || []).map(
    (m: {
      hotel_id: number;
      client_name: string;
      ref_client: string | null;
      checkin_date: string;
      checkout_date: string;
      nights: number | null;
      estimated_revenue: unknown;
      realised_revenue: unknown;
      commission_percent: number | null;
    }) => {
      const commOverride =
        m.commission_percent == null ? null : Number(m.commission_percent);
      const commEffective =
        commOverride ?? hotelDefaultCommissionById.get(m.hotel_id) ?? '';
      const realised =
        m.realised_revenue != null && m.realised_revenue !== ''
          ? Number(m.realised_revenue)
          : null;
      const realisedForInvoice = realised;
      const pctNum = commEffective === '' ? null : Number(commEffective);
      const invoiceAmount =
        pctNum != null && !Number.isNaN(pctNum) && realisedForInvoice != null
          ? realisedForInvoice * (pctNum / 100)
          : '';
      return {
        Origin: 'Additional',
        'Hotel ID': m.hotel_id,
        'Hotel name': hotelNameById.get(m.hotel_id) ?? '',
        'Group ID': '',
        Client: m.client_name ?? '',
        'Ref. client': m.ref_client?.trim() || '',
        'Check-in': formatDateOnly(m.checkin_date),
        'Check-out': formatDateOnly(m.checkout_date),
        Nights: m.nights == null ? '' : Number(m.nights),
        'Estimated Revenue': Number(m.estimated_revenue) || 0,
        'Realised Revenue': realised ?? '',
        'Commission %': commEffective === '' ? '' : commEffective,
        'Invoice amount': invoiceAmount,
      };
    },
  );

  const exportRows = [...groupExportRows, ...manualSheetRows];

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Campaign');
  const safeName = (campaignName || `${FULL_MONTHS[periodMonth - 1]}_${periodYear}`)
    .replace(/[/\\?*[\]]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);
  XLSX.writeFile(wb, `revenue_collection_${safeName}.xlsx`);
}
